
/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/ActionsCreators/ServiceEndpointsActionsCreator" />

import * as React from "react";
import { Tooltip } from "VSSUI/TooltipEx";

import { ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { Action } from "Environments/Flux/ActionCreatorBase";
import { ServiceEndpointsActions, IServiceEndpointItemList } from "Environments/ProviderCreateDetails/Kubernetes/Actions/ServiceEndpointsActions";
import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import {
    ServiceEndpoint,
    AzureSubscriptionQueryResult,
    ServiceEndpointRequestResult,
    ServiceEndpointRequest,
    ServiceEndpointDetails,
    EndpointAuthorization,
    AzureSubscription,
    AccessTokenRequestType
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { IVssPageContext } from "VSS/Platform/Context";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { PageKey, setAuthInEnpointRequest } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";
import { GuidUtils } from "Environments/EnvironmentsCommon/GuidUtils";
import { getErrorMessage } from "Environments/EnvironmentsCommon/Utils";
import { AadAuthorizer } from "Environments/EnvironmentsCommon/AadAuthorizer";
import { IProjectData } from "Tfs/Platform/Page";
import { AccessTokenActions } from "Environments/ProviderCreateDetails/Kubernetes/Actions/AccessTokenActions";

export class ServiceEndpointsActionsCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "service-endpoints-action-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);

        this._actions = ActionsHubManager.CreateActionsHub<ServiceEndpointsActions>(ServiceEndpointsActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions>(MessagesActions, instanceId);
        this._accessTokenActions = ActionsHubManager.GetActionsHub<AccessTokenActions>(AccessTokenActions, instanceId);
    }

    public getServiceEndpoints(pageContext: IVssPageContext, type: string): void {
        const environmentService = getEnvironmentService(pageContext);
        this._updateMessageArea("");
        environmentService.getServiceEndpoints(type).then((endpoints: ServiceEndpoint[]) => {
            this._actions.getServiceEndpoints.invoke(endpoints);
        },
            (error) => {
                this._updateMessageArea(getErrorMessage(error));
            });
    }

    public getAzureSubscriptions(pageContext: IVssPageContext): void {
        this._updateMessageArea("");
        const environmentService = getEnvironmentService(pageContext);
        environmentService.getAzureSubscriptions().then((result: AzureSubscriptionQueryResult) => {
            if (!result.errorMessage) {
                this._actions.getAzureSubscriptions.invoke(result.value);
            }
            else {
                this._updateMessageArea(result.errorMessage);
            }
        },
            (error) => {
                this._updateMessageArea(getErrorMessage(error));
            });
    }

    public getAADTenantId(pageContext: IVssPageContext): void {
        const environmentService = getEnvironmentService(pageContext);
        this._updateMessageArea("");
        environmentService.getAADTenantId().then((tenantId: string) => {
            this._actions.getTenantId.invoke(tenantId);
        },
            (error: any) => {
                this._updateMessageArea(getErrorMessage(error));
            })
    }

    public getAzureKubernetesClusters(pageContext: IVssPageContext, azureSubsubscription: AzureSubscription | undefined, tenantId: string | undefined, accessTokens: { [key: string]: string }): void {
        this._getServiceEndpointRequestData(pageContext, azureSubsubscription, undefined, tenantId, accessTokens, "AzureKubernetesClusters", undefined, this._actions.getKubernetesClusters);
    }

    public getAzureKubernetesNamespaces(pageContext: IVssPageContext, azureSubsubscription: AzureSubscription | undefined, tenantId: string | undefined, accessTokens: { [key: string]: string }, clusterId: string, clusterFqdn: string): void {
        const serviceEndpointDetails = this._getServiceEndpointDetailsForNamespaces(clusterId, clusterFqdn, azureSubsubscription, undefined);
        this._getServiceEndpointRequestData(pageContext, azureSubsubscription, undefined, tenantId, accessTokens, "KubernetesNamespaces", serviceEndpointDetails, this._actions.getKubernetesClusterNamespaces);
    }

    private _getServiceEndpointRequestData(
        pageContext: IVssPageContext,
        azureSubsubscription: AzureSubscription | undefined,
        endpoint: ServiceEndpoint | undefined,
        tenantId: string | undefined,
        accessTokens: { [key: string]: string },
        dataSourceName: string,
        connectionDetails: ServiceEndpointDetails | undefined,
        dataFetchedAction: Action<IServiceEndpointItemList>,
        callbackInvocation: boolean = false,
        callbackParameters: { [key: string]: string } = {}
    ): void {

        const fetchEndpointRequestResult = () => {
            environmentService.executeServiceEndpointRequestResult(serviceEndpointRequest, connectionId).then((serviceRequestEndpointResult: ServiceEndpointRequestResult) => {
                if (!!serviceRequestEndpointResult.errorMessage) {
                    // In case of an error, set the list to an empty list
                    dataFetchedAction.invoke({ data: [], append: false, callbackParameters: {} });

                    this._updateMessageArea(getErrorMessage(serviceRequestEndpointResult.errorMessage));
                }
                else {

                    dataFetchedAction.invoke({ data: serviceRequestEndpointResult.result, append: callbackInvocation, callbackParameters: serviceRequestEndpointResult.callbackContextParameters });

                    if (serviceRequestEndpointResult.callbackRequired) {
                        this._getServiceEndpointRequestData(
                            pageContext,
                            azureSubsubscription,
                            endpoint,
                            tenantId,
                            accessTokens,
                            dataSourceName,
                            connectionDetails,
                            dataFetchedAction,
                            true,
                            serviceRequestEndpointResult.callbackContextParameters
                        );
                    }
                }
            },
                (error: any) => {
                    // In case of an error, set the list to an empty list
                    dataFetchedAction.invoke({ data: [], append: false, callbackParameters: {} });

                    this._updateMessageArea(getErrorMessage(error));
                });
        }

        let serviceEndpointDetails: ServiceEndpointDetails = connectionDetails || this._getServiceEndpointDetails(azureSubsubscription, endpoint);
        let connectionId = (!!endpoint) ? endpoint.id : "0";

        let serviceEndpointRequest: ServiceEndpointRequest = {
            serviceEndpointDetails: (!!endpoint) ? null as any as ServiceEndpointDetails : serviceEndpointDetails,
            dataSourceDetails: {
                dataSourceName: dataSourceName,
                dataSourceUrl: "",
                headers: [],
                requestContent: "",
                requestVerb: "",
                resourceUrl: "",
                parameters: callbackInvocation ? callbackParameters : {},
                resultSelector: "",
                initialContextTemplate: ""
            },
            resultTransformationDetails: {
                resultTemplate: "",
                callbackContextTemplate: "",
                callbackRequiredTemplate: ""
            }
        };

        const environmentService = getEnvironmentService(pageContext);
        this._updateMessageArea("");

        if (azureSubsubscription && tenantId && tenantId != azureSubsubscription.subscriptionTenantId) {
            let aadAuthorizer = new AadAuthorizer(pageContext);
            const fetchEndpointRequestResultWithAccessToken = (accessTokenKey: string) => {
                setAuthInEnpointRequest(pageContext, serviceEndpointRequest.serviceEndpointDetails, accessTokenKey, true);
                fetchEndpointRequestResult();
            }

            if (!accessTokens[azureSubsubscription.subscriptionTenantId]) {

                const projectData: IProjectData | undefined = PageContextHelper.getProject(pageContext);
                aadAuthorizer.getAccessTokenKey(projectData, azureSubsubscription.subscriptionTenantId, tenantId, undefined, null).then((accessTokenKey: string) => {
                    this._accessTokenActions.getAccessToken.invoke({
                        accessTokenKey: accessTokenKey,
                        resourceTenantId: azureSubsubscription.subscriptionTenantId
                    });
                    fetchEndpointRequestResultWithAccessToken(accessTokenKey);
                });

            } else {
                fetchEndpointRequestResultWithAccessToken(accessTokens[azureSubsubscription.subscriptionTenantId]);
            }
        } else {
            fetchEndpointRequestResult();
        }
    }



    private _getServiceEndpointDetails(
        azureSubsubscription?: AzureSubscription,
        endpoint?: ServiceEndpoint
    ): ServiceEndpointDetails {

        let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;

        if (azureSubsubscription) {
            let endpointAuthorization: EndpointAuthorization = {
                parameters: {
                    accessTokenType: "AppToken",
                    tenantId: azureSubsubscription.subscriptionTenantId
                },
                scheme: "serviceprincipal"
            }

            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    id: GuidUtils.newGuid(),
                    name: GuidUtils.newGuid(),
                    subscriptionId: azureSubsubscription.subscriptionId,
                    subscriptionName: azureSubsubscription.displayName,
                    environment: "AzureCloud",
                    scopeLevel: "subscription"
                },
                type: "azurerm",
                url: "https://management.azure.com/"
            }
        } else {
            serviceEndpointDetails = endpoint
        }

        return serviceEndpointDetails!;
    }

    private _getServiceEndpointDetailsForNamespaces(
        clusterId: string,
        clusterFqdn: string,
        azureSubsubscription?: AzureSubscription,
        endpoint?: ServiceEndpoint
    ): ServiceEndpointDetails {

        let serviceEndpointDetails: ServiceEndpointDetails | undefined = undefined;

        if (azureSubsubscription) {
            let endpointAuthorization: EndpointAuthorization = {
                parameters: {
                    azureEnvironment: "AzureCloud",
                    azureTenantId: azureSubsubscription.subscriptionTenantId
                },
                scheme: "Kubernetes"
            }

            serviceEndpointDetails = {
                authorization: endpointAuthorization,
                data: {
                    authorizationType: "AzureSubscription",
                    azureSubscriptionId: azureSubsubscription.subscriptionId,
                    azureSubscriptionName: azureSubsubscription.displayName,
                    clusterId: clusterId
                },
                type: "kubernetes",
                url: clusterFqdn
            }
        } else {
            serviceEndpointDetails = endpoint
        }

        return serviceEndpointDetails!;
    }

    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<ServiceEndpointsActions>(ServiceEndpointsActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _updateMessageArea(message: string): void {
        const messageElement = message ? React.createElement(Tooltip, { text: message },
            React.createElement("div", { className: "k8s-resource-message" }, message)) : null;
        const invokeAction = () => this._messageActions.addMessage.invoke({ key: PageKey.AddKubernetesResourceView, value: messageElement });
        if (message) {
            invokeAction();
        }
        else {
            // Clearing the message bar occurs outside promises, and therefore ocassionally lands us into action within an action errors
            setTimeout(invokeAction, 0);
        }
    }

    private _actions: ServiceEndpointsActions;
    private _messageActions: MessagesActions;
    private _accessTokenActions: AccessTokenActions;
}

function getEnvironmentService(pageContext: IVssPageContext): IEnvironmentService {
    return pageContext.getService<IEnvironmentService>("IEnvironmentService");
}